import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { camelizeKeys, decamelizeKeys } from "humps";

export const API_ROOT = process.env.API_ROOT;

export type ApiError = AxiosError<
  { errors: string[] } | { error: string } | any
>;

export type Params = {
  [key: string]: string;
};

export type Options = {
  shouldCamelize: boolean;
};

export type Api = {
  get<T>(path: string, params?: Params, options?: Options): Promise<T>;
  postFormData<T>(path: string, formData: any, options?: Options): Promise<T>;
  post<T>(path: string, body: any, options?: Options): Promise<T>;
  patch<T>(path: string, body: any, options?: Options): Promise<T>;
  put<T>(path: string, body: any, options?: Options): Promise<T>;
  delete<T>(path: string, params?: Params, options?: Options): Promise<T>;
  request<T>(requestConfig: AxiosRequestConfig, options?: Options): Promise<T>;
};

export type ApiConfig = {
  provideJwtToken: () => string | null;
  getParsedConfigurations: () => any;
};

export default (config: ApiConfig) => {
  const createHeaders: (
    provideJwt: () => string | null
  ) =>
    | { Authorization: string; "Access-Control-Allow-Origin": string }
    | { "Access-Control-Allow-Origin": string } = (
    provideJwt: () => string | null
  ) =>
    provideJwt()
      ? {
          Authorization: `Bearer ${provideJwt()}`,
          "Access-Control-Allow-Origin": "*"
        }
      : {
          "Access-Control-Allow-Origin": "*"
        };

  const api: Api = {
    get(path, params, options) {
      return axios
        .get(API_ROOT + path, {
          params,
          headers: createHeaders(config.provideJwtToken)
        })
        .then(response => handleResponse(response, options));
    },
    postFormData(path, formData, options) {
      return axios
        .post(API_ROOT + path, formData, {
          headers: {
            ...createHeaders(config.provideJwtToken)
          }
        })
        .then(response => handleResponse(response, options));
    },
    post(path, body, options) {
      return axios
        .post(API_ROOT + path, body, {
          headers: createHeaders(config.provideJwtToken)
        })
        .then(response => handleResponse(response, options));
    },
    patch(path, body, options) {
      return axios
        .patch(API_ROOT + path, decamelizeKeys(body), {
          headers: createHeaders(config.provideJwtToken)
        })
        .then(response => handleResponse(response, options));
    },
    put(path, body, options) {
      return axios
        .put(API_ROOT + path, decamelizeKeys(body), {
          headers: createHeaders(config.provideJwtToken)
        })
        .then(response => handleResponse(response, options));
    },
    delete(path, params, options) {
      return axios
        .delete(API_ROOT + path, {
          params,
          headers: createHeaders(config.provideJwtToken)
        })
        .then(response => handleResponse(response, options));
    },
    request(requestConfig, options) {
      return axios
        .request(
          Object.assign(requestConfig, {
            // @ts-ignore
            url: API_ROOT + requestConfig.url,
            data: requestConfig.data ? camelizeKeys(requestConfig.data) : null,
            headers: createHeaders(config.provideJwtToken)
          })
        )
        .then(response => handleResponse(response, options));
    }
  };

  return api;
};

export function handleResponse(
  response: AxiosResponse<object[]>,
  shouldCamelize?: Options | undefined
) {
  const camelizedData = shouldCamelize
    ? camelizeKeys(response.data)
    : response.data;

  // Quickfix to prevent underscored dependencies from being camelized.
  // Never store data as keys in the future.
  if (
    // @ts-ignore
    camelizedData &&
    // @ts-ignore
    camelizedData.data &&
    // @ts-ignore
    camelizedData.data.npmDependencies
  ) {
    // @ts-ignore
    camelizedData.data.npmDependencies = response.data.data.npm_dependencies;
  }

  // @ts-ignore
  return camelizedData.data ? camelizedData.data : camelizedData;
}
